/* ---------------------------------------------------------------------------
   The programme as a calendar: one month at a time, each activity a bar across
   the days it is planned for.

   The other way to draw either of the Scheduler's two views, chosen by the
   switch in the Programme panel's head (`scheduleLayout` in the store). The
   Gantt view's calendar shows the frozen plan, critical activities marked; the
   planned activity view's shows the same dates coloured by where each activity
   has got to on site. Group rows are left out — a WBS summary across a month is
   a bar over every other bar.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type Child } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { addDays } from '../data/periods.ts';
import { prettyDate } from '../data/derive.ts';

/** One bar, inclusive of both dates. */
export interface CalendarItem {
  id: string;
  name: string;
  start: string;
  finish: string;
  tone: 'plan' | 'crit' | 'good' | 'active' | 'late' | 'idle';
  title: string;
}

/** The legend under the month: the tones this view uses, and what they mean. */
export type CalendarLegend = { tone: CalendarItem['tone']; label: string }[];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** Bars a week shows before the rest are counted as "+N more". */
const LANES = 6;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** "2026-05" from a date. */
function monthOf(iso: string): string {
  return iso.slice(0, 7);
}

function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return d.toISOString().slice(0, 7);
}

/** Monday on or before a date. */
function mondayOf(iso: string): string {
  const dow = (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;
  return addDays(iso, -dow);
}

/**
 * The month to open on: the one asked for, or this month where the programme
 * runs through it, or else the month it starts.
 */
function shownMonth(s: Readonly<State>, items: readonly CalendarItem[]): string {
  if (s.scheduleMonth && s.scheduleMonth.bridgeId === s.selectedId) return s.scheduleMonth.month;
  const now = todayIso();
  const first = items.reduce((a, i) => (i.start < a ? i.start : a), items[0]?.start ?? now);
  const last = items.reduce((a, i) => (i.finish > a ? i.finish : a), items[0]?.finish ?? now);
  return monthOf(now >= first && now <= last ? now : first);
}

/** Place each week's bars into lanes, earliest first, none overlapping. */
function lanes(items: readonly CalendarItem[], weekStart: string, weekEnd: string) {
  const inWeek = items
    .filter((i) => i.start <= weekEnd && i.finish >= weekStart)
    .sort((a, b) => (a.start === b.start ? b.finish.localeCompare(a.finish) : a.start.localeCompare(b.start)));
  const ends: string[] = [];
  const placed: { item: CalendarItem; lane: number; from: number; to: number }[] = [];
  const hidden = new Array<number>(7).fill(0);
  for (const item of inWeek) {
    const s = item.start < weekStart ? weekStart : item.start;
    const e = item.finish > weekEnd ? weekEnd : item.finish;
    const from = dayIndex(weekStart, s);
    const to = dayIndex(weekStart, e);
    let lane = ends.findIndex((end) => end < s);
    if (lane === -1) lane = ends.length;
    if (lane >= LANES) {
      for (let d = from; d <= to; d++) hidden[d]!++;
      continue;
    }
    ends[lane] = e;
    placed.push({ item, lane, from, to });
  }
  return { placed, hidden, used: Math.min(ends.length, LANES) };
}

function dayIndex(weekStart: string, iso: string): number {
  return Math.round((Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${weekStart}T00:00:00Z`)) / 86_400_000);
}

export function programmeCalendar(
  s: Readonly<State>,
  items: readonly CalendarItem[],
  legend: CalendarLegend,
): HTMLElement {
  const month = shownMonth(s, items);
  const [y, m] = month.split('-').map(Number) as [number, number];
  const first = `${month}-01`;
  const lastDay = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  const now = todayIso();

  const weeks: string[] = [];
  for (let w = mondayOf(first); w <= lastDay; w = addDays(w, 7)) weeks.push(w);

  const inMonth = items.filter((i) => i.start <= lastDay && i.finish >= first).length;

  const nav = (by: number, label: string, path: Parameters<typeof icon>[0]) =>
    h(
      'button',
      {
        type: 'button',
        class: 'progcal__nav',
        title: label,
        'aria-label': label,
        onclick: () => actions.setScheduleMonth(shiftMonth(month, by)),
      },
      icon(path, { size: 14, width: 1.8 }),
    );

  return h(
    'div',
    { class: 'progcal' },
    h(
      'div',
      { class: 'progcal__bar' },
      nav(-1, 'Previous month', ICONS.chevronLeft),
      nav(1, 'Next month', ICONS.chevronRight),
      h('span', { class: 'progcal__month', text: `${MONTHS[m - 1]} ${y}` }),
      h('button', {
        type: 'button',
        class: 'progcal__today',
        text: 'Today',
        onclick: () => actions.setScheduleMonth(monthOf(now)),
      }),
      h('span', { class: 'progcal__count num', text: `${inMonth} ${inMonth === 1 ? 'activity' : 'activities'} this month` }),
      h('span', { class: 'spacer' }),
      ...legend.map((l) =>
        h('span', { class: 'progcal__key' }, h('span', { class: `progcal__swatch progcal--${l.tone}` }), l.label),
      ),
    ),
    h(
      'div',
      { class: 'progcal__grid' },
      h('div', { class: 'progcal__head' }, ...WEEKDAYS.map((d) => h('div', { class: 'progcal__dow', text: d }))),
      ...weeks.map((w) => week(w, items, first, lastDay, now)),
    ),
  );
}

function week(weekStart: string, items: readonly CalendarItem[], first: string, lastDay: string, now: string): Child {
  const weekEnd = addDays(weekStart, 6);
  const { placed, hidden, used } = lanes(items, weekStart, weekEnd);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return h(
    'div',
    { class: 'progcal__week', style: { '--lanes': String(Math.max(used, 1)) } },
    ...days.map((d, i) =>
      h(
        'div',
        {
          class: [
            'progcal__day',
            (d < first || d > lastDay) && 'progcal__day--out',
            d === now && 'progcal__day--today',
            i >= 5 && 'progcal__day--weekend',
          ],
          style: { gridColumn: String(i + 1) },
        },
        h('span', { class: 'progcal__date num', text: String(Number(d.slice(8))) }),
      ),
    ),
    /* Under the lanes, in a row of their own, so they never sit on a bar. */
    ...days.map((_, i) =>
      hidden[i]
        ? h('span', {
            class: 'progcal__more',
            text: `+${hidden[i]} more`,
            style: { gridColumn: String(i + 1), gridRow: String(used + 2) },
          })
        : null,
    ),
    ...placed.map(({ item, lane, from, to }) =>
      h(
        'div',
        {
          class: [
            'progcal__ev',
            `progcal--${item.tone}`,
            item.start < weekStart && 'progcal__ev--cont',
            item.finish > weekEnd && 'progcal__ev--more',
          ],
          style: { gridColumn: `${from + 1} / ${to + 2}`, gridRow: String(lane + 2) },
          title: `${item.id} · ${item.name}\n${prettyDate(item.start)} → ${prettyDate(item.finish)}\n${item.title}`,
        },
        h('span', { class: 'progcal__ev-id num', text: item.id }),
        h('span', { class: 'progcal__ev-name', text: item.name }),
      ),
    ),
  );
}
